import type { PrintConfig, ReceiptData, ReceiptDetail } from '@matix/types';
import { usesBilingualReceipt, receiptModifierName } from './bilingualReceipt.js';

/**
 * Turn a stored receipt into the slip that gets printed again.
 *
 * This is the reprint mapping, and it lived inside the cashier's
 * `useReprintReceipt` until the kitchen screen needed to print a customer
 * invoice too. Moving it here rather than copying it is the whole point: the
 * paper a customer is handed must not depend on which screen the staff member
 * happened to be standing at, and a second copy of this mapping is a second
 * document that drifts — the kind of difference nobody notices until a VAT
 * audit.
 *
 * `buildReceiptData` is the sibling of this function and is NOT the same thing:
 * that one composes a slip from live cart state at charge time, where the app
 * still holds barcodes and the tender the cashier keyed. This one starts from
 * what the database kept, which is less — no barcodes — and more, because it
 * also has to represent things only a finished receipt has: returns, the
 * exchange summary, named promotions. The item CODE travels on both paths:
 * the receipt detail carries Inv_Items.Code so a design printing a code
 * column prints the same sheet at charge time and on a reprint.
 */
export function buildReprintData(
  printConfig: PrintConfig,
  detail: ReceiptDetail,
  /**
   * The language the slip will print in, when the caller knows it.
   *
   * Only the MODIFIER names need it here: everything else on a line carries
   * both scripts through to `buildReceiptHtml`, which picks there. Options
   * cannot — `ReceiptData.mods` is a flat `string[]` by the time it reaches the
   * renderer, so the choice has to be made while both halves are still in hand.
   *
   * Resolved exactly like `receiptArabic`: an explicit language wins, otherwise
   * the tenant's own ArabiReset flag. Omitting it keeps every existing caller
   * byte-identical.
   */
  language?: 'en' | 'ar',
): ReceiptData {
  const arabic = language ? language === 'ar' : printConfig.printOptions?.arabic === true;
  // A bilingual tenant prints BOTH halves of every option, the receipt's own
  // language first — the same rule the rest of that slip follows.
  const bilingual = usesBilingualReceipt(printConfig);
  // MAIN ITEMS ONLY. A receipt can carry component lines beside the items the
  // customer ordered — the ERP explodes a finished product into its
  // ingredients, and a modifier option with a linked item deducts what it
  // consumes (migration 059). Both are written at price 0 as pure stock
  // movements, so printing them padded the slip with rows the customer never
  // ordered and every one of them priced 0. They stay in the receipt detail
  // (stock, edits, the order views and the return path all need them); they
  // are simply not part of the document. The kitchen has always filtered the
  // same two columns, and so do the reports.
  const printedLines = detail.lines.filter((l) => l.is_component !== true);
  // Code 53 is the return leg. A receipt can also carry return lines without
  // being one, which is why both are checked.
  const isReturnReceipt = detail.code === 53 || printedLines.some((l) => l.is_return);

  return {
    printConfig,
    orderId: detail.id,
    orderCode: detail.entry_no,
    entryDate: detail.entry_date,
    cashier: {
      id: detail.empoyee_id ?? 0,
      name: detail.empoyee_name,
      nameAr: detail.empoyee_name_ar ?? null,
    },
    customer: detail.customer_name
      ? {
          type: 'cash',
          name: detail.customer_name,
          nameAr: detail.customer_name_ar,
          phone: detail.customer_phone,
          // Picks a design's tax-invoice title, as on the first print.
          ...(detail.customer_tax_number ? { taxNumber: detail.customer_tax_number } : {}),
          // The address the order went to. Both scripts travel; which one
          // prints is decided by `buildReceiptHtml`, like every other name on
          // the slip. Absent on an older API build, and on a walk-in.
          ...(detail.customer_address
            ? {
                addressDetail: {
                  city: detail.customer_address.city,
                  cityAr: detail.customer_address.city_ar,
                  district: detail.customer_address.district,
                  districtAr: detail.customer_address.district_ar,
                  block: detail.customer_address.block,
                  street: detail.customer_address.street,
                  avenue: detail.customer_address.avenue,
                  house: detail.customer_address.house,
                  floor: detail.customer_address.floor,
                  flat: detail.customer_address.flat,
                  note: detail.customer_address.note,
                },
              }
            : {}),
        }
      : undefined,
    items: printedLines.map((line) => ({
      name: line.item_name,
      nameAr: line.item_name_ar,
      // The item number. A custom A4 design can give its items table a code
      // column, and a reprint without this one prints it blank — a different
      // sheet to the one the customer was handed, which is the whole reason
      // this mapping is shared.
      code: line.item_code ?? null,
      qty: line.qty,
      price: line.unit_price,
      // The unit, named only on a multi-unit item — the same rule the
      // charge-time slip applies, so the reprint reads the same.
      ...(line.has_multiple_units
        ? { unit: line.unit_name ?? null, unitAr: line.unit_name_ar ?? null }
        : {}),
      discount: line.discount_value > 0 ? line.discount_value : undefined,
      total: line.is_return ? -Math.abs(line.line_total) : line.line_total,
      // The options the customer chose, and their note — the same two things
      // the charge-time slip prints, and gated by the same tenant flag
      // (IsPrintCustomOptions) downstream. A reprint that drops them prints a
      // different document to the one the customer was handed, which is
      // exactly what this file exists to prevent.
      // ONE language per option, the slip's own. The legacy ERP options carry
      // both halves (`option_name` / `option_name_ar`); a title that could not
      // be split holds the same string in both, so this still prints what it
      // always did. pos-api's own modifiers leave `option_name_ar` null and
      // fall back to `option_name`, unchanged.
      ...(line.modifiers && line.modifiers.length > 0
        ? {
            mods: line.modifiers
              .map((m) =>
                receiptModifierName(
                  { name: m.option_name, nameAr: m.option_name_ar },
                  { arabic, bilingual },
                ),
              )
              .filter((n) => n.length > 0),
          }
        : {}),
      ...(line.notes ? { note: line.notes } : {}),
      // Who sold the line — the charge-time slip prints it, so the reprint
      // must too. Both scripts travel; the renderer picks per language.
      ...(line.salesman_name || line.salesman_name_ar
        ? { salesman: line.salesman_name ?? null, salesmanAr: line.salesman_name_ar ?? null }
        : {}),
      ...(line.is_return ? { isReturn: true } : {}),
    })),
    totals: {
      subtotal: detail.value,
      discount: detail.discount_value,
      deliveryFee: detail.delivery_fees,
      tax: detail.tax_value,
      total: detail.net_value,
    },
    ...(isReturnReceipt
      ? {
          exchange: {
            soldTotal: 0,
            returnedTotal: Math.abs(detail.net_value),
            netDifference: -Math.abs(detail.net_value),
            returnEntryNo: detail.entry_no,
          },
        }
      : {}),
    payments: detail.payments.map((p) => ({
      typeName: p.payment_type_label,
      amount: p.amount,
    })),
    changeDue: detail.change_amount > 0 ? detail.change_amount : undefined,
    // Pre-formatted "Sun bed: 5" / "Table: 7" from the receipt detail — the
    // template renders it under the cashier metadata, same as the original
    // print at charge time. null for walk-in / takeaway.
    tableLabel: detail.table_label,
    // The receipt's own Memo (cashier Edit dialog / ERP memo box) — the A4
    // sheets print it in their Memo box. Never a synthesised payment summary.
    memo: detail.memo ?? null,
    // The till's "Reference no." and "Notes" boxes. `reference_no` here is the
    // Memo column, not `Inv_Receipt_h.Reference_No` — the ERP's report prints
    // Memo under المرجع and Reference_No under الملاحظات, and the API resolves
    // the note out of whichever column this receipt keeps it in.
    referenceNo: detail.memo ?? null,
    notes: detail.notes ?? null,
    // Only a delivery order carries a promised time worth printing; on every
    // other type the column merely echoes Entry_Date.
    deliveryDate: detail.order_type === 2 ? detail.delivery_date ?? null : null,
    // The order type itself, so a design heading its ticket 'Delivery/توصيل'
    // reprints the same heading. Null on a legacy receipt that never had one.
    orderType: detail.order_type ?? null,
    // The ERP's issue counter, straight off the row — so a reprint of an
    // edited receipt shows the same marker the edited copy carried, and a
    // reprint of an unedited one adds nothing. Reprinting never bumps it;
    // the legacy till does not either.
    printCount: detail.print_count ?? null,
    // Who last edited it and when — from the change log, so every reprint of
    // an edited invoice names the same person and time.
    editedBy: detail.last_edit?.by_name ?? null,
    editedByAr: detail.last_edit?.by_name_ar ?? null,
    editedAt: detail.last_edit?.at ?? null,
    // Named promotions, so the reprint explains its discount the same way the
    // original slip did. Their money is already inside `totals.discount` — the
    // template prints them under it, indented, and never adds them to anything.
    // Omitted entirely when there are none, so a receipt without promotions
    // reprints byte-for-byte as before.
    ...(detail.promotions && detail.promotions.length > 0
      ? {
          promotions: detail.promotions.map((p) => ({
            name: p.name,
            ...(p.name_ar ? { nameAr: p.name_ar } : {}),
            amount: p.discount_amount,
            ...(p.level ? { level: p.level } : {}),
          })),
        }
      : {}),
    isReprint: true,
  };
}
